(() => {
  const selector = '[data-ca-card-video] video, .m-collection-banner__media video, .slim-banner__inner video';
  const states = new Map();
  const visible = new Set();
  const smallScreen = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let scheduled = false;

  const stop = (video, state) => {
    state.generation += 1;
    state.pending = false;
    if (!video.paused) video.pause();
    state.card?.classList.remove('is-playing');
    if (state.card && state.loaded) {
      video.querySelectorAll('source[src]').forEach(source => source.removeAttribute('src'));
      video.removeAttribute('src');
      state.loaded = false;
      video.load();
    }
  };

  const play = (video, state) => {
    if (state.blocked || state.pending || !video.paused) return;
    if (state.card && !state.loaded) {
      video.querySelectorAll('source[data-src]').forEach(source => {
        source.setAttribute('src', source.dataset.src);
      });
      state.loaded = true;
      video.load();
    }
    state.pending = true;
    const generation = ++state.generation;
    let result;
    try {
      result = video.play();
    } catch (error) {
      state.pending = false;
      state.blocked = true;
      return;
    }
    Promise.resolve(result).then(() => {
      if (generation !== state.generation) return;
      state.pending = false;
      if (!video.paused) state.card?.classList.add('is-playing');
    }).catch(() => {
      if (generation !== state.generation) return;
      state.pending = false;
      state.blocked = true;
      state.card?.classList.remove('is-playing');
    });
  };

  const sync = () => {
    scheduled = false;
    let cardCount = 0;
    const limit = smallScreen.matches ? 2 : 4;
    const staticCards = reducedMotion.matches || navigator.connection?.saveData;
    for (const [video, state] of states) {
      if (!video.isConnected) {
        stop(video, state);
        observer?.unobserve(video);
        visible.delete(video);
        states.delete(video);
        continue;
      }
      let shouldPlay = !document.hidden && visible.has(video);
      if (state.card) {
        shouldPlay = shouldPlay && !staticCards && cardCount < limit && !state.blocked;
        if (shouldPlay) cardCount += 1;
      }
      if (shouldPlay) play(video, state);
      else if (state.loaded || state.pending || !video.paused) stop(video, state);
    }
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(sync);
  };

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.15) visible.add(entry.target);
      else visible.delete(entry.target);
    }
    schedule();
  }, { threshold: [0, 0.15], rootMargin: '0px' }) : null;

  const prepare = video => {
    if (states.has(video)) return;
    const card = video.closest('[data-ca-card-video]');
    states.set(video, { card, loaded: false, blocked: false, pending: false, generation: 0 });
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    if (card) {
      video.autoplay = false;
      video.controls = false;
      video.preload = 'none';
    }
    observer?.observe(video);
  };

  const scan = root => {
    if (root.matches?.(selector)) prepare(root);
    root.querySelectorAll?.(selector).forEach(prepare);
  };

  const initialize = () => {
    scan(document);
    new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        }
      }
      schedule();
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else initialize();

  document.addEventListener('shopify:section:load', event => {
    scan(event.target);
    schedule();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) sync();
    else schedule();
  });
  window.addEventListener('pageshow', schedule);
  smallScreen.addEventListener('change', schedule);
  reducedMotion.addEventListener('change', schedule);
  ['pointerdown', 'keydown'].forEach(eventName => {
    document.addEventListener(eventName, () => {
      for (const video of visible) {
        const state = states.get(video);
        if (state) state.blocked = false;
      }
      sync();
    }, { passive: true });
  });
})();
